import React, { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

type TruncatedUrlCellProps = {
  url: string;
};

const urlTypographySx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
} as const;

/** Renders a URL truncated with ellipsis; full value shown on hover only when truncated. */
export const TruncatedUrlCell: FC<TruncatedUrlCellProps> = ({ url }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOverflow = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    setIsOverflowing(element.scrollWidth > element.clientWidth);
  }, []);

  useLayoutEffect(() => {
    updateOverflow();
  }, [url, updateOverflow]);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateOverflow]);

  const typography = (
    <Typography ref={textRef} variant="body2" sx={urlTypographySx}>
      {url}
    </Typography>
  );

  if (!isOverflowing) {
    return typography;
  }

  return (
    <Tooltip title={url} arrow placement="top">
      {typography}
    </Tooltip>
  );
};

export default TruncatedUrlCell;
